import {documentToReactComponents} from '@contentful/rich-text-react-renderer'
import {BLOCKS, MARKS} from '@contentful/rich-text-types'
import {Box, Stack, Text, Button} from '@primer/react-brand/esm'

import {getAnalyticsEvent, toSnakeCase} from '@github-ui/swp-core/lib/utils/analytics'
import type {RichText} from '@github-ui/swp-core/schemas/contentful/richText'

import type {GenericContent} from '../../../../../../brand/lib/types/contentful'
import {
  PLAN_TYPES,
  type PlanType,
} from '../../../../../../brand/components/PlanTypeSegmentedControl/PlanTypeSegmentedControl'

import styles from './styles.module.css'

function renderRichText(richText: RichText) {
  return documentToReactComponents(richText, {
    renderMark: {
      [MARKS.BOLD]: text => (
        <Text as="span" variant="default" weight="semibold" className={styles.title}>
          {text}
        </Text>
      ),
    },
    renderNode: {
      [BLOCKS.PARAGRAPH]: (_, children) => (
        <Text as="span" variant="muted">
          {children}
        </Text>
      ),
    },
  })
}

type Props = {
  contentfulContent: GenericContent
  columns: GenericContent[]
  planType: PlanType
}

export function CompareTableHeader(props: Props) {
  const {contentfulContent, columns, planType} = props
  const {heading} = contentfulContent.fields

  const plans = {
    [PLAN_TYPES.Individual]: ['Free', 'Pro', 'ProPlus', 'Max'],
    [PLAN_TYPES.Business]: ['Business', 'Enterprise'],
  }

  const filteredTableColumns = columns.filter(column => plans[planType].includes(column.fields.id ?? ''))

  return (
    <Stack direction="horizontal" gap={32} padding="none" className={styles.root}>
      <Box className={styles.heading}>
        <Text size="500">{heading}</Text>
      </Box>

      <Stack role="rowgroup" direction="vertical" gap={4} padding="none" className={styles.columns}>
        <Stack role="row" direction="horizontal" gap={32} padding="none">
          <div role="columnheader" className={styles.columnHeaderLabel}>
            <span className="sr-only">Pricing plans</span>
          </div>

          {filteredTableColumns.map(column => {
            const columnLink = column.fields.links?.at(0)

            return (
              <div key={column.sys.id} role="columnheader" className={styles.column}>
                {column.fields.heading ? (
                  <Text as="p" size="300" weight="medium">
                    {column.fields.heading}
                  </Text>
                ) : null}

                {column.fields.subheading ? (
                  <Text as="p" size="100" weight="normal" className={styles.subheading}>
                    {renderRichText(column.fields.subheading)}
                  </Text>
                ) : null}

                {columnLink ? (
                  <Button
                    as="a"
                    href={columnLink.fields.href}
                    variant={columnLink.fields.variant || 'primary'}
                    size="small"
                    {...getAnalyticsEvent({
                      action: toSnakeCase(columnLink.fields.text),
                      tag: 'button',
                      context: `${toSnakeCase(column.fields.id || '')}_plan`,
                      location: 'comparison_table',
                    })}
                  >
                    {columnLink.fields.text}
                  </Button>
                ) : null}
              </div>
            )
          })}
        </Stack>
      </Stack>
    </Stack>
  )
}
